import { WorkspacePremium } from '@mui/icons-material';
import {
    Box, Breadcrumbs,
    Button,
    CircularProgress,
    Grid,
    Typography
} from '@mui/material';
import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";
import api from '../../api';
import DidNotLoad from "../../components/DidNotLoad";
import ImageUpload from "../../components/ImageUpload";
import MoneyInputWithBlur from "../../components/MoneyInputWithBlur";
import PaymentMethodSelect from "../../components/checkout/PaymentMethodSelect";

function QuickCheckoutPage() {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [amount, setAmount] = useState(0);
    const navigate = useNavigate();
    const [resultErrors, setResultErrors] = useState({
        has_errors: false,
        customer_name: null,
        payment_methods: null,
    });
    const [hasResponse, setHasResponse] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const timerRef = useRef(null); // Usado para armazenar o timer
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('Crédito');


    const handleSubmit = async (event) => {
        event.preventDefault();

        if (isSubmitting) return; // Evita duplo clique
        setIsSubmitting(true); // Ativa o estado de loading

        // Debug removido - estava causando confusão

        try {
            setResultErrors({
                has_errors: false,
            });

            let allRequiredFieldsOk = true;
            let invalidData = {};

            if (!allRequiredFieldsOk) {
                setResultErrors({
                    ...resultErrors,
                    ...invalidData,
                    has_errors: true,
                });
                return
            }

            const newCheckout = {
                amount: (amount * 100),
            };

            let redirectTo = '/admin/checkouts'

            // Prepara os dados para envio
            let requestData;
            let requestConfig = {};

            // Se há uma foto, usar FormData para multipart/form-data
            if (photo) {
                const formData = new FormData();
                formData.append('amount', (amount * 100).toString());
                formData.append('photo', photo);
                if (paymentMethod !== "") {
                    formData.append('payment_type', paymentMethod);
                }

                requestData = formData;
                // Não definir Content-Type: o axios inclui o boundary do multipart automaticamente.
                requestConfig = {};
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Nenhuma Imagem',
                    text: 'Nenhuma imagem foi enviada! Valor da foto: ' + (photo ? 'existe' : 'null'),
                });
                setIsSubmitting(false);
                return;
            }

            api.post('quick-checkout', requestData, requestConfig)
                .then((response) => {
                    const data = response.data;
                    if (!data?.success) {
                        setResultErrors({
                            ...data.errors,
                            has_errors: true,
                        });
                    } else {
                        navigate(redirectTo); // Redireciona de volta para a lista
                    }
                    setHasResponse(true);
                })
                .catch((error) => {
                    // Verifica se error.response existe antes de acessar error.response.data
                    if (error.response && error.response.data) {
                        const data = error.response.data;
                        setResultErrors({
                            ...data.errors,
                            has_errors: true,
                        });
                    } else {
                        // Para erros de rede, timeout ou outros erros sem response
                        Swal.fire({
                            icon: 'error',
                            title: 'Erro de Conexão',
                            text: 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.',
                        });
                    }
                    setHasResponse(true);
                })
                .finally(() => {
                    setIsSubmitting(false); // Desativa o estado de loading
                    if (!hasResponse) {
                        setDidNotLoad(true);
                    }
                });
        } catch (error) {
            console.error('Erro geral na função handleSubmit:', error);
            Swal.fire({
                icon: 'error',
                title: 'Erro Inesperado',
                text: 'Ocorreu um erro inesperado. Tente novamente.',
            });
            setIsSubmitting(false);
        }
    };

    const handlePhotoChange = (file, preview) => {
        setPhoto(file);
        setPhotoPreview(preview);
    };


    if (didNotLoad) {
        return <DidNotLoad />
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/orders')} to={'/admin/checkouts'}>
                    Checkouts
                </Link>
                <Typography color="text.primary">Quick Checkout</Typography>
            </Breadcrumbs>
            <br />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h4">Quick Checkout</Typography>
                <WorkspacePremium color="warning" />
            </Box>
            <form onSubmit={handleSubmit}>
                <Grid item xs={12} sm={12}>
                    <ImageUpload
                        label="Foto dos produtos vendidos"
                        value={photoPreview}
                        onChange={handlePhotoChange}
                        maxSize={2 * 1024 * 1024} // 2MB (reduzido para forçar compressão)
                        compressImage={true}
                        maxWidth={1920}
                        maxHeight={1080}
                        quality={0.8}
                    />
                </Grid>

                <Box mb={4} />

                <Grid container spacing={3}>
                    <Box mb={2} />

                    <Grid item xs={12} sm={6}>
                        <MoneyInputWithBlur
                            label="Valor total da venda"
                            value={amount}
                            onChange={(values) => setAmount(values.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <PaymentMethodSelect
                            label="Meio de pagamento"
                            value={paymentMethod}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                        />
                    </Grid>

                    {resultErrors.payment_methods && (
                        <Grid item xs={12} sm={12}>
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {resultErrors.payment_methods}
                            </div>
                        </Grid>
                    )}
                </Grid>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' }, // Empilha no mobile, lado a lado em telas maiores
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: 2,
                        marginBottom: 8,
                        gap: 2 // Espaçamento entre os itens
                    }}
                >
                    {/* Mensagem de erro à esquerda */}
                    {resultErrors.has_errors ? (
                        <div
                            className="text-danger"
                            style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                        >
                            Ocorreram erros de validação, verifique os campos acima.
                        </div>
                    ) : (
                        <div style={{ height: '1rem' }} />
                    )}

                    {/* Botões responsivos */}
                    <Grid container spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        <Grid item xs={12} sm="auto">
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                disabled={isSubmitting}
                                startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
                                fullWidth // Garante que o botão fique 100% em telas pequenas
                            >
                                {isSubmitting ? 'Registrando...' : 'Registrar'}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </form>
        </div>
    );
}

export default QuickCheckoutPage;
